import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Picture } from "@/components/site/Picture";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { BRAND } from "@/lib/brand";
import coleHero from "@/assets/cole-method-hero.jpg";
import { DirectAnswer, FAQSection, faqSchema } from "@/components/site/AnswerBlock";
import { COLE_METHOD_ANSWER, COLE_METHOD_FAQS } from "@/lib/aeo";

export const Route = createFileRoute("/the-cole-method")({
  head: () => ({
    meta: [
      { title: "The COLE Method - Real Estate Coaching for Agents" },
      {
        name: "description",
        content:
          "The COLE Method is Kristan Cole's four-pillar framework for real estate coaching for agents, taught inside our real estate mastermind and across KCN.",
      },
      { property: "og:title", content: "The COLE Method™" },
      {
        property: "og:description",
        content:
          "A four-pillar framework - Clarity, Operations, Leverage, Expansion - for building a real estate business that lasts.",
      },
      { property: "og:url", content: "https://joinkcn.com/the-cole-method" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/the-cole-method" }],
    scripts: [
      breadcrumbSchema([{ name: "The COLE Method", path: "/the-cole-method" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "The COLE Method™",
          provider: { "@type": "Organization", name: "Kristan Cole Network" },
          description:
            "A four-pillar real estate growth framework - Clarity, Operations, Leverage, Expansion - used to scale agents from solo producer to team CEO.",
          serviceType: "Real Estate Agent Coaching & Team Framework",
          areaServed: ["Arizona", "Alaska", "United States"],
        }),
      },
      faqSchema(COLE_METHOD_FAQS, "https://joinkcn.com/the-cole-method"),
    ],
  }),
  component: ColeMethodPage,
});

const pillars = [
  {
    letter: "C",
    name: "Clarity",
    headline: "Define the business that fits your calling.",
    body: "Most agents grow before they know what they're growing toward. Clarity comes first: the role you actually want to play, the income that funds the life you want, the scope of business you're called to build. Without it, every system feels like noise.",
    moves: [
      "Vision + values alignment audit",
      "Income & lifestyle target setting",
      "Role design - what only you should be doing",
    ],
  },
  {
    letter: "O",
    name: "Operations",
    headline: "Install the systems that remove you as the bottleneck.",
    body: "Once you're clear, you install the operating system. Lead-flow, conversion, transaction management, client experience, finance - documented, repeatable, transferable. The goal isn't to work less; it's to make sure the work that happens isn't all you.",
    moves: [
      "Lead-gen + conversion playbook",
      "Transaction & client-experience SOPs",
      "Weekly operating rhythm",
    ],
  },
  {
    letter: "L",
    name: "Leverage",
    headline: "Hire and lead the people who multiply your output.",
    body: "Leverage is where most teams break. Hiring without a system. Managing without a framework. We install the org chart, the scorecards, and the leadership cadence that lets people do their best work - so growth doesn't mean more chaos.",
    moves: [
      "Org design & first-five-hires roadmap",
      "Scorecards, accountability, 1:1 cadence",
      "Faith-aligned leadership development",
    ],
  },
  {
    letter: "E",
    name: "Expansion",
    headline: "Scale into a legacy business that pays you back.",
    body: "Expansion is the ceiling-breaker. New markets, new offerings, equity, partnerships. The COLE Method™ ends where most agents never get to start: a business that funds your life, your family, your causes - long after you've stopped grinding.",
    moves: [
      "Multi-market & multi-team expansion",
      "Wealth-building & equity strategy",
      "Legacy + succession planning",
    ],
  },
];

function ColeMethodPage() {
  return (
    <SiteLayout>
      <section className="relative pt-36 pb-14 border-b border-border overflow-hidden isolate">
        <div className="absolute inset-0 -z-10">
          <Picture
            src={coleHero}
            alt=""
            aria-hidden="true"
            pictureClassName="block h-full w-full"
            className="h-full w-full object-cover scale-105"
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/70 to-background/95" />
          <div className="absolute inset-0 bg-background/35" />
        </div>
        <div className="container-edge">
          <p className="eyebrow mb-6">The Framework</p>
          <h1 className="headline-xl max-w-5xl">
            The COLE Method<span className="text-primary">™</span>
          </h1>
          <p className="mt-8 max-w-3xl font-display italic text-2xl md:text-3xl text-foreground/90 leading-snug">
            The operating system behind "decide to do what others won't" - 
            Kristan's branded path to wealth and freedom.
          </p>
          <p className="mt-6 max-w-2xl text-foreground/90 text-lg leading-relaxed">
            Four pillars for turning a real estate{" "}
            <em className="serif-italic">producer</em> into a real estate{" "}
            <em className="serif-italic">CEO</em>. It's how every agent on the
            Kristan Cole Network team builds a business that lasts.
          </p>
        </div>
      </section>

      <DirectAnswer
        question={COLE_METHOD_ANSWER.question}
        answer={COLE_METHOD_ANSWER.answer}
      />


      <Section tone="dark">
        <p className="eyebrow mb-6 text-center">Hover · Click to explore</p>
        <div className="grid md:grid-cols-4 gap-px bg-border border border-border">
          {pillars.map((p, i) => (
            <a
              key={p.letter}
              href={`#pillar-${p.letter.toLowerCase()}`}
              className="group relative bg-background p-10 flex flex-col gap-4 overflow-hidden cursor-pointer transition-colors duration-500 hover:bg-primary"
            >
              {/* Giant letter - slides up on hover */}
              <span className="relative font-display text-[8rem] leading-none text-primary group-hover:text-primary-foreground transition-all duration-500 group-hover:-translate-y-2">
                {p.letter}
              </span>

              {/* Default label */}
              <div className="transition-all duration-500 group-hover:opacity-0 group-hover:-translate-y-2">
                <p className="eyebrow group-hover:text-primary-foreground/80">{p.name}</p>
                <p className="font-display text-xl leading-snug mt-3">{p.headline}</p>
              </div>

              {/* Reveal on hover */}
              <div className="absolute inset-x-0 bottom-0 p-10 pt-6 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <p className="text-xs uppercase tracking-[0.22em] text-primary-foreground/70 mb-2">
                  Pillar 0{i + 1}
                </p>
                <p className="font-display text-3xl text-primary-foreground leading-tight mb-3">
                  {p.name}
                </p>
                <p className="text-sm text-primary-foreground/90 leading-relaxed">
                  {p.body.split(".")[0]}.
                </p>
                <p className="mt-4 text-xs uppercase tracking-[0.22em] text-primary-foreground font-medium">
                  Read more ↓
                </p>
              </div>

              {/* Index tick */}
              <span className="absolute top-4 right-5 text-xs tabular-nums text-foreground/40 group-hover:text-primary-foreground/60 transition-colors">
                0{i + 1} / 04
              </span>
            </a>
          ))}
        </div>
      </Section>

      {pillars.map((p, i) => (
        <Section key={p.letter} tone={i % 2 === 0 ? "darker" : "dark"} id={`pillar-${p.letter.toLowerCase()}`}>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="flex items-baseline gap-6">
                <span className="font-display text-[9rem] leading-none text-primary">
                  {p.letter}
                </span>
                <div>
                  <p className="eyebrow mb-2">Pillar 0{i + 1}</p>
                  <h2 className="font-display text-4xl">{p.name}</h2>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <h3 className="headline-md">{p.headline}</h3>
              <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
                {p.body}
              </p>
              <ul className="mt-10 divide-y divide-border border-y border-border">
                {p.moves.map((m) => (
                  <li
                    key={m}
                    className="py-4 flex items-center gap-4 text-foreground/85"
                  >
                    <span className="text-primary text-xs">◆</span>
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      ))}

      {/* MEET THE ARCHITECT */}
      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-border">
              <Picture
                src={BRAND.profilePics[0]}
                alt="Kristan Cole - founder of the Kristan Cole Network and architect of the COLE Method"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="absolute -bottom-4 -right-4 hidden md:block bg-primary text-primary-foreground px-6 py-3 font-display text-sm tracking-wider uppercase">
              The Architect
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionEyebrow>Built by an operator, not a guru</SectionEyebrow>
            <h2 className="headline-lg mb-6">
              Forty years.
              <br />
              <span className="serif-italic text-primary">One framework.</span>
            </h2>
            <p className="text-foreground/90 text-lg leading-[1.7] mb-5">
              The COLE Method™ isn't theory. It's the operating system
              Kristan Cole built over four decades - through Alaska winters,
              market crashes, single-motherhood, and thousands of closings - to
              take agents from chaos to clarity, and producer to CEO.
            </p>
            <p className="text-foreground/75 leading-[1.7] mb-8">
              Every pillar above was forged in the field. Every system is one
              she still runs today.
            </p>
            <Link
              to="/kristan"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-primary hover:text-foreground transition-colors border-b border-primary/40 hover:border-foreground pb-1"
            >
              Read Kristan's full story →
            </Link>
          </div>
        </div>
      </Section>


      <Section tone="cream">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>The Outcome</SectionEyebrow>
            <h2 className="headline-lg">
              A business that
              <br />
              <span className="serif-italic text-primary">funds your life.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-cream-foreground/85 text-lg leading-relaxed">
            <p>
              Agents who run the COLE Method™ stop being the bottleneck. They
              stop trading hours for income. They build something that compounds - 
              and that they can hand to their kids.
            </p>
            <p className="font-display italic text-2xl text-cream-foreground">
              "From chaos to clarity. From agent to CEO."
            </p>
          </div>
        </div>
      </Section>

      <FAQSection
        faqs={COLE_METHOD_FAQS}
        heading="About the COLE Method."
        disclaimerQuestions={["How long does it take to see results?"]}
      />

      <CTABanner
        cta="roundtable"
        title="Want to see the Method in action?"
        body="The Monthly Market Round Table is where Kristan walks her team through real applications - every month. Request an invitation."
      />
    </SiteLayout>
  );
}
